<template>
  <b-form v-if="validations !== undefined" @submit.stop.prevent="onSubmit" novalidate>
    <div v-html="sanitizedContent(title)"></div>
    <hr />
    <b-form-group v-for="item of visibleFormFields" label-class="input-label" :class="subtypeClass(item)"
      :key="item.inputId" :id="`${item.inputId}-group`" :description="item.description" :label="item.label"
      :label-for="item.inputId">
      <!-- If type=select -->
      <b-form-select v-if="item.type === 'select'" :id="item.inputId" :name="item.inputName"
        v-model="$v.formModel[item.inputName].$model" :state="validateState(item.inputName)"
        :aria-describedby="`${item.inputId}-live-feedback`" :options="item.options"></b-form-select>

      <!-- ElseIf type=checkbox -->
      <b-form-checkbox v-else-if="item.type === 'checkbox'" :id="item.inputId"
        v-model="$v.formModel[item.inputName].$model" :state="validateState(item.inputName)"
        :aria-describedby="`${item.inputId}-live-feedback`" :name="item.inputName"
      >{{ item.options[0]?.text }}</b-form-checkbox>

      <!-- ElseIf type=checkbox-group -->
      <b-form-checkbox-group v-else-if="item.type === 'checkbox-group'" :id="item.inputId"
        v-model="$v.formModel[item.inputName].$model" :state="validateState(item.inputName)"
        :aria-describedby="`${item.inputId}-live-feedback`" :options="item.options" :name="item.inputName"
        stacked></b-form-checkbox-group>

      <!-- ElseIf type=textarea -->
      <b-form-textarea v-else-if="item.type === 'textarea'" trim :id="item.inputId" :name="item.inputName" :disabled="item.disabled"
        :placeholder="item.placeholder" v-model="$v.formModel[item.inputName].$model"
        :state="validateState(item.inputName)" :aria-describedby="`${item.inputId}-live-feedback`">
      </b-form-textarea>

      <!-- Else-If type=signature -->
      <signature-pad v-else-if="item.type == 'signature'" :id="item.inputId" :name="item.inputName" @sign="getSign" />

      <!-- If type=date -->
      <b-form-input v-else-if="item.type == 'date'" :id="item.inputId" :name="item.inputName" :placeholder="item.placeholder" :disabled="item.disabled"
        v-model="$v.formModel[item.inputName].$model" type="text" autocomplete="off"
        :aria-describedby="`${item.inputId}-live-feedback`" :state="validateState(item.inputName)"></b-form-input>

      <!-- Else, render the input type as it is -->
      <b-form-input v-else :type="item.type" trim :id="item.inputId" :name="item.inputName" :disabled="item.disabled"
        :placeholder="item.placeholder" v-model="$v.formModel[item.inputName].$model"
        :state="validateState(item.inputName)" :aria-describedby="`${item.inputId}-live-feedback`">
      </b-form-input>

      <!-- Error Messages -->
      <b-form-invalid-feedback :id="`${item.inputId}-live-feedback`">
        <div v-for="(message, ruleKey) in validationRuleMessages?.[item.inputName]" :key="ruleKey"
          v-show="!$v.formModel[item.inputName][ruleKey]">
          {{ message }}
        </div>
      </b-form-invalid-feedback>
    </b-form-group>
    <div class="contact">
      <div class="contact__info" v-html="sanitizedContent(thankYouMessage)"></div>
    </div>
    <b-row>
      <b-col>
        <b-button-group class="mt-5">
          <b-button class="default-button mr-2" @click="$emit('back')">{{ cancelFormButton.text }}</b-button>
          <b-button class="default-button" type="submit">{{ okFormButton.text }}</b-button>
          <loading :show="loading" />
        </b-button-group>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { msmApi, strapi } from '@/api'
import Validators, { buildValidator } from '@/utils/customFormValidations'
import SignaturePad from './SignaturePad.vue'
import Loading from '../Loading.vue'

const touchMap = new WeakMap()

export default {
  components: { 
    Loading,
    SignaturePad
  },
  props: {
    formDefaultValues: Object,
    locale: String
  },
  data() {
    return {
      loading: false,
      form: [],
      formModel: {},
      validationRuleMessages: {},
      title: '',
      thankYouMessage: '',
      okFormButton: {},
      cancelFormButton: {},
      validations: undefined
    }
  },
  created() {
    strapi
      .getConsentProcessDocumentation(this.locale)
      .then(([form, validationRules]) => {
        this.title = form.title
        this.thankYouMessage = form.thankYouMessage
        this.okFormButton = form.okFormButton
        this.cancelFormButton = form.cancelFormButton
        this.formModel = form.form.reduce((model, curr) => (
          {
            ...model, [curr.inputName]:
              curr.multiple
                ?
                curr.defaultValue
                  ? [curr.defaultValue]
                  : []
                : curr.defaultValue
          }
        ), {})
        const dateArray = new Date().toISOString().split('T')[0].split('-')
        const dateString = [...dateArray.slice(-2), ...dateArray.slice(0, 1)].join('-')
        const timeString = new Date().toLocaleTimeString()
        const todayDate = `${dateString} ${timeString}`
        this.formModel = {...this.formModel, cpd_date: todayDate}
        this.form = form.form
        this.validationRuleMessages = validationRules
      })
      .then(() => {
        this.validations = Object
          .entries(this.validationRuleMessages)
          .reduce((validations, [inputName, rulesObj]) =>
          ({
            ...validations, [inputName]: Object.keys(rulesObj)
              .filter(key => key in Validators)
              .reduce((validators, key) => ({
                ...validators,
                [key]: buildValidator(key, this.form.find(i => i.inputName === inputName), this.formModel)
              }),
                {}
              )
          }), {}
          )
      })
  },
  computed: {

    visibleFormFields() {
      return this.form.filter(
        item => {
          if (!item.dependOnFieldId) {
            return true
          }
          // TODO: check with Tom
          const values = item.dependOnFieldValue.split(',')
          return values.some(v => this.$v.formModel[item.dependOnFieldId]?.$model?.includes(v))
        }
      )
    },
  },
  validations() {
    return {
      formModel: this.validations || {}
    }
  },
  methods: {
    subtypeClass(input) {
      return input.dependOnFieldId ? 'input-subtype' : ''
    },
    sanitizedContent(content) {
      return DOMPurify.sanitize(marked(content))
    },
    validateState(name) {
      const fieldModel = this.$v.formModel[name]
      return fieldModel?.$dirty ? !fieldModel?.$error : null
    },
    delayTouch($v) {
      $v.$reset()
      if (touchMap.has($v)) {
        clearTimeout(touchMap.get($v))
      }
      touchMap.set($v, setTimeout($v.$touch, 500))
    },
    getSign(inputName, signInfo) {
      const { isEmpty, data } = signInfo
      this.$v.formModel[inputName].$model = isEmpty ? null : data
    },
    onSubmit() {
      this.$v.formModel.$touch()

      if (this.$v.formModel.$anyError) {
        return
      }
      this.loading = true
      msmApi
          .signatureForm({ ...this.formDefaultValues, consentProcessDocumentation: this.formModel }).then(() => {
            this.$emit('submit', this.formModel)
          })
          .catch((error) => {
            this.$gtag.exception({
              description: JSON.stringify(error),
              fatal: true
            })
          })
          .finally(() => {
            this.loading = false
          })
      }
  }
}
</script>

<style>
.submit--btn {
  background-color: #269cd2;
  color: #fefefe !important;
}

.input-label {
  font-weight: 600 !important;
}

.contact {
  display: flex;
  flex-direction: column;
  background-color: rgba(56, 97, 136, 1);
  height: 100%;
  width: 100%;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.contact__info p {
  font-size: 1.25rem;
  font-weight: 500 !important;
  margin: 0;
  padding: 0;
  color: #fff !important;
}

.input-subtype {
  margin-left: 1.5rem;
}
</style>
